import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CompanyDomain,
  useCompanyDomainCreateMutation,
  useCompanyDomainDestroyMutation,
  useCompanyDomainVerifyMutation,
  useSsoIntegrationsDomainCheckLazyQuery,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import useSearchParam from '@lib/hooks/useSearchParam';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { Values } from '@lib/interfaces/form';
import useCompanyDomainsQueryHook from 'utils/fetch/hooks/useCompanyDomainsQueryHook';
import { ADD_DOMAIN_FIELD_NAME } from './getAddDomainFormFields';

export default function useActions(props: {
  useModalManagementResult: UseModalManagementResult;
}) {
  const { useModalManagementResult } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const { closeModal, openModal } = useModalManagementResult;

  const [selectedItem, setSelectedItem] = useState<CompanyDomain | null>(null);
  const [isDomainUsedBySso, setIsDomainUsedBySso] = useState(false);
  const [fetchSsoCheck] = useSsoIntegrationsDomainCheckLazyQuery();

  const companyDomainsQueryHookResult = useCompanyDomainsQueryHook({
    searchQuery,
    paginationParams,
    orderingParams: paginationParams,
  });

  const [createMutation, createMutationResult] = useCompanyDomainCreateMutation(
    {
      onCompleted: () => {
        closeModal(Modals.CompanyDomainFormModal);
        toast({ content: t('domain-added-success') });
        companyDomainsQueryHookResult.refetch();
      },
      onError: (error) => {
        toast({ content: error.message || t('something-went-wrong') });
      },
    },
  );

  const [verifyMutation, verifyMutationResult] = useCompanyDomainVerifyMutation(
    {
      onCompleted: () => {
        closeModal(Modals.CompanyDomainVerifyModal);
        toast({ content: t('domain-verified-success') });
        companyDomainsQueryHookResult.refetch();
      },
      onError: (error) => {
        toast({ content: error.message || t('something-went-wrong') });
      },
    },
  );

  const [destroyMutation] = useCompanyDomainDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.CompanyDomainDestroyModal);
      toast({ content: t('domain-deleted-success') });
      onDestroyListItemCallBack({
        collection: companyDomainsQueryHookResult.collection,
        navigate,
        pathname,
        refetch: companyDomainsQueryHookResult.refetch,
        search,
        totalPages: companyDomainsQueryHookResult.totalPages,
      });
    },
    onError: (error) => {
      toast({ content: error.message || 'Oops! Something went wrong' });
    },
  });

  const onAddDomain = () => {
    openModal(Modals.CompanyDomainFormModal);
  };

  const onSubmitAddDomain = (values: Values) => {
    createMutation({
      variables: {
        attributes: {
          domain: values[ADD_DOMAIN_FIELD_NAME] as string,
        },
      },
    });
  };

  const onVerify = (item?: CompanyDomain) => {
    if (item?.id) {
      setSelectedItem(item);
      openModal(Modals.CompanyDomainVerifyModal);
    }
  };

  const onConfirmedVerify = () => {
    if (selectedItem?.id) {
      verifyMutation({ variables: { id: selectedItem.id } });
    }
  };

  const onDelete = async (item?: CompanyDomain) => {
    if (item?.id) {
      setSelectedItem(item);
      try {
        const { data } = await fetchSsoCheck({
          variables: {
            limit: 1,
            filters: [{ companyDomainsId: { predicate: { eq: item.id } } }],
          },
          fetchPolicy: 'network-only',
        });
        setIsDomainUsedBySso((data?.data?.collection?.length ?? 0) > 0);
      } catch {
        setIsDomainUsedBySso(false);
      }
      openModal(Modals.CompanyDomainDestroyModal);
    }
  };

  const onConfirmedDelete = () => {
    closeModal(Modals.CompanyDomainDestroyModal);
    if (selectedItem?.id) {
      destroyMutation({
        variables: {
          id: selectedItem.id,
        },
      });
    }
  };

  return {
    companyDomainsQueryHookResult,
    createMutationResult,
    isDomainUsedBySso,
    onAddDomain,
    onConfirmedDelete,
    onConfirmedVerify,
    onDelete,
    onSubmitAddDomain,
    onVerify,
    selectedItem,
    verifyMutationLoading: verifyMutationResult.loading,
  };
}
